<template>
  <q-page class="achievements-page">
    <div class="achievements-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div style="display: flex; align-items: flex-end; justify-content: space-between;">
          <div>
            <q-skeleton type="text" width="160px" dark style="font-size: 1.75rem;" />
            <q-skeleton type="text" width="260px" dark style="margin-top: 4px;" />
          </div>
          <q-skeleton type="rect" width="120px" height="32px" dark style="border-radius: 8px;" />
        </div>
        <q-skeleton type="rect" height="64px" dark style="border-radius: 12px; margin-top: 24px;" />
        <div class="skeleton-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 8" :key="n" type="rect" height="180px" dark style="border-radius: 12px;" />
        </div>
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="achievements-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchAllData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="ach-header">
          <div>
            <h1 class="ach-title">Logros</h1>
            <p class="ach-subtitle">
              {{ profile.achievementsUnlocked }}/{{ totalVisible }} desbloqueados
              &middot; {{ completionPct }}%
            </p>
          </div>
          <div class="ach-header-stats">
            <span class="stat-item">
              <q-icon name="bolt" size="14px" style="color: var(--oa-gold-full);" />
              <strong>{{ formatNumber(profile.xp) }}</strong>
            </span>
            <span class="stat-item">
              <q-icon name="local_fire_department" size="14px" style="color: var(--color-terracotta);" />
              <strong>{{ profile.currentStreak }}</strong> dias
            </span>
          </div>
        </div>

        <!-- Tabs -->
        <div class="ach-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="ach-tab"
            :class="{ 'ach-tab-active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            <q-icon :name="tab.icon" size="16px" />
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab 1: Logros -->
        <div v-if="activeTab === 'logros'" class="tab-content">
          <!-- Profile card -->
          <div class="profile-card">
            <div class="profile-card-left">
              <div class="profile-avatar">
                <q-icon name="person" size="28px" />
              </div>
              <div>
                <p class="profile-level-name">{{ profile.levelName }}</p>
                <p class="profile-level-sub">Nivel {{ profile.level }}</p>
              </div>
            </div>
            <div class="profile-card-right">
              <div class="xp-bar-wrap">
                <div class="xp-bar-labels">
                  <span>{{ formatNumber(profile.xp) }} AURA</span>
                  <span>{{ profile.xpProgress }}%</span>
                </div>
                <div class="xp-bar-track">
                  <div class="xp-bar-fill" :style="{ width: profile.xpProgress + '%' }" />
                </div>
                <p class="xp-bar-hint">{{ formatNumber(profile.xpToNextLevel) }} para siguiente nivel</p>
              </div>
              <div class="streak-display">
                <q-icon name="local_fire_department" size="20px" style="color: var(--color-terracotta);" />
                <span class="streak-count">{{ profile.currentStreak }}</span>
                <span class="streak-label">racha</span>
              </div>
            </div>
          </div>

          <!-- Overall progress -->
          <div class="progress-bar-section">
            <div class="progress-bar-header">
              <span>Progreso general</span>
              <span class="progress-bar-pct">{{ completionPct }}%</span>
            </div>
            <div class="progress-bar-track">
              <div class="progress-bar-fill" :style="{ width: completionPct + '%' }" />
            </div>
            <!-- Rarity legend -->
            <div class="rarity-legend">
              <span v-for="r in rarityLegend" :key="r.label" class="rarity-item">
                <span class="rarity-dot" :style="{ background: r.color }" />
                {{ r.label }}
              </span>
            </div>
          </div>

          <!-- Achievement categories -->
          <div v-for="cat in categories" :key="cat.key" class="ach-category">
            <div class="cat-header">
              <div class="cat-header-left">
                <q-icon :name="categoryIcons[cat.key] || 'star'" size="16px" style="color: var(--oa-gold-mid);" />
                <h2 class="cat-label">{{ cat.label }}</h2>
                <span class="cat-count">{{ cat.unlockedCount }}/{{ cat.items.length }}</span>
              </div>
              <div class="cat-progress-track">
                <div
                  class="cat-progress-fill"
                  :style="{
                    width: (cat.items.length > 0 ? Math.round(cat.unlockedCount / cat.items.length * 100) : 0) + '%',
                    background: cat.unlockedCount === cat.items.length && cat.items.length > 0 ? 'var(--oa-tier-cc)' : 'var(--oa-gold-mid)',
                  }"
                />
              </div>
            </div>

            <div class="ach-grid">
              <div
                v-for="ach in cat.items"
                :key="ach.id"
                class="ach-card"
                :class="{ 'ach-unlocked': ach.isUnlocked, 'ach-locked': !ach.isUnlocked }"
                :style="{
                  borderColor: ach.isUnlocked ? getRarity(ach.xpReward).color : undefined,
                  boxShadow: ach.isUnlocked ? getRarity(ach.xpReward).glow : undefined,
                }"
              >
                <!-- Icon -->
                <div
                  class="ach-icon-wrap"
                  :style="{
                    background: ach.isUnlocked
                      ? `color-mix(in srgb, ${getRarity(ach.xpReward).color} 12%, transparent)`
                      : 'var(--oa-bg-3)',
                    color: ach.isUnlocked ? getRarity(ach.xpReward).color : 'var(--oa-text-disabled)',
                  }"
                >
                  <q-icon
                    :name="isHidden(ach) ? 'lock' : achievementIcon(ach.icon, ach.category)"
                    size="20px"
                  />
                </div>

                <!-- Name -->
                <p class="ach-name">{{ isHidden(ach) ? '???' : ach.name }}</p>

                <!-- Greek name -->
                <p
                  v-if="ach.greekName && !isHidden(ach)"
                  class="ach-greek"
                  :style="{ color: getRarity(ach.xpReward).color }"
                >
                  {{ ach.greekName }}
                </p>

                <!-- Description -->
                <p class="ach-desc">
                  {{ isHidden(ach) ? 'Logro secreto' : ach.description }}
                </p>

                <!-- Reward -->
                <div class="ach-reward-row">
                  <span
                    class="ach-reward-badge"
                    :class="ach.isUnlocked ? 'badge-gold' : 'badge-outline'"
                  >
                    +{{ ach.xpReward }} AURA
                  </span>
                </div>

                <!-- Rarity -->
                <div v-if="!isHidden(ach)" class="ach-rarity">
                  <span class="rarity-dot" :style="{ background: getRarity(ach.xpReward).color }" />
                  <span :style="{ color: getRarity(ach.xpReward).color }">{{ getRarity(ach.xpReward).tier }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Collectibles section -->
          <div v-if="collectibles.length > 0" class="collectibles-section">
            <h2 class="section-heading">
              <q-icon name="stars" size="16px" style="color: var(--oa-gold-mid);" />
              Coleccionables
            </h2>
            <div class="collectibles-grid">
              <div
                v-for="col in collectibles"
                :key="col.id"
                class="collectible-card"
                :class="{ 'collectible-earned': col.isEarned }"
              >
                <div class="collectible-icon">
                  <q-icon :name="col.isEarned ? 'stars' : 'lock'" size="24px" />
                </div>
                <p class="collectible-name">{{ col.name }}</p>
                <p v-if="col.greekName" class="collectible-greek">{{ col.greekName }}</p>
                <p v-if="col.rankTitle && col.isEarned" class="collectible-rank">{{ col.rankTitle }}</p>
                <p class="collectible-desc">{{ col.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab 2: Ranking (Leaderboard) -->
        <div v-if="activeTab === 'ranking'" class="tab-content">
          <!-- Filters -->
          <div class="lb-filters">
            <div class="lb-toggle-group">
              <button
                v-for="s in scopeOptions"
                :key="s.value"
                class="lb-toggle"
                :class="{ 'lb-toggle-active': lbScope === s.value }"
                @click="lbScope = s.value"
              >
                {{ s.label }}
              </button>
            </div>
            <div class="lb-toggle-group">
              <button
                v-for="p in periodOptions"
                :key="p.value"
                class="lb-toggle"
                :class="{ 'lb-toggle-active': lbPeriod === p.value }"
                @click="lbPeriod = p.value"
              >
                {{ p.label }}
              </button>
            </div>
          </div>

          <!-- Leaderboard list -->
          <div v-if="lbLoading" class="lb-card">
            <div
              v-for="n in 5"
              :key="n"
              class="lb-row-skeleton"
            >
              <q-skeleton type="circle" size="28px" dark />
              <q-skeleton type="circle" size="36px" dark />
              <div style="flex: 1;">
                <q-skeleton type="text" width="120px" dark />
                <q-skeleton type="rect" height="4px" dark style="border-radius: 2px; margin-top: 6px;" />
              </div>
              <q-skeleton type="text" width="60px" dark />
            </div>
          </div>

          <div v-else-if="lbEntries.length === 0" class="lb-card lb-empty">
            <p style="color: var(--oa-text-muted);">Sin datos de leaderboard.</p>
            <p style="font-size: 11px; color: var(--oa-text-disabled); margin-top: 4px;">
              Gana AURA para aparecer
            </p>
          </div>

          <div v-else class="lb-card">
            <div
              v-for="(entry, i) in lbEntries.slice(0, 10)"
              :key="entry.rank"
              class="lb-row"
              :class="{ 'lb-row-me': entry.isCurrentUser }"
              :style="{ borderBottom: i < Math.min(lbEntries.length, 10) - 1 ? '1px solid var(--oa-border)' : undefined }"
            >
              <span
                class="lb-rank"
                :class="{ 'lb-rank-top': i < 3 }"
              >
                <q-icon v-if="i === 0" name="emoji_events" size="16px" style="color: var(--color-terracotta);" />
                <q-icon v-else-if="i === 1" name="emoji_events" size="16px" style="color: #a0a0a0;" />
                <q-icon v-else-if="i === 2" name="emoji_events" size="16px" style="color: #cd7f32;" />
                <span v-else>{{ entry.rank }}</span>
              </span>

              <div class="lb-avatar">
                <q-icon name="person" size="18px" />
              </div>

              <div class="lb-info">
                <p class="lb-name" :style="{ color: entry.isCurrentUser ? 'var(--oa-gold-mid)' : 'var(--oa-text-primary)' }">
                  {{ entry.user?.firstName ?? '' }} {{ entry.user?.lastName ?? '' }}
                  <span v-if="entry.isCurrentUser" class="lb-me-badge">(vos)</span>
                </p>
                <div class="lb-stats-row">
                  <span>{{ entry.lessonsCompleted }} lecciones</span>
                  <span>{{ entry.quizzesPassed }} quizzes</span>
                </div>
                <!-- Source breakdown bar -->
                <div v-if="(entry.totalBreakdown ?? 0) > 0" class="lb-breakdown-bar">
                  <div
                    v-for="(value, source) in entry.sourceBreakdown"
                    :key="source"
                    class="lb-breakdown-segment"
                    :style="{
                      width: ((value / (entry.totalBreakdown ?? 1)) * 100) + '%',
                      backgroundColor: sourceColors[source as string] ?? '#94a3b8',
                    }"
                    :title="`${source}: ${value} AURA`"
                  />
                </div>
              </div>

              <span
                class="lb-xp"
                :style="{ color: i < 3 ? 'var(--oa-gold-mid)' : 'var(--oa-text-muted)' }"
              >
                {{ entry.xp }} AURA
              </span>
            </div>
          </div>
        </div>

        <!-- Tab 3: AURA -->
        <div v-if="activeTab === 'aura'" class="tab-content">
          <!-- Source breakdown -->
          <div class="aura-breakdown-card">
            <h3 class="aura-section-title">Distribucion de AURA por fuente</h3>
            <div v-if="auraSources.length === 0" class="aura-empty-sources">
              <p>Sin datos de AURA aun.</p>
            </div>
            <div v-else class="aura-sources-list">
              <div
                v-for="src in auraSources"
                :key="src.source"
                class="aura-source-row"
              >
                <div class="aura-source-label">
                  <span
                    class="aura-source-dot"
                    :style="{ background: sourceColors[src.source] ?? '#94a3b8' }"
                  />
                  <span>{{ sourceLabels[src.source] ?? src.source }}</span>
                </div>
                <div class="aura-source-bar-track">
                  <div
                    class="aura-source-bar-fill"
                    :style="{
                      width: (auraTotalPoints > 0 ? (src.total / auraTotalPoints) * 100 : 0) + '%',
                      background: sourceColors[src.source] ?? '#94a3b8',
                    }"
                  />
                </div>
                <span class="aura-source-value">{{ src.total }}</span>
              </div>
            </div>
          </div>

          <!-- AURA History -->
          <div class="aura-history-card">
            <h3 class="aura-section-title">
              <q-icon name="history" size="16px" style="color: var(--oa-gold-mid);" />
              Historial AURA
            </h3>

            <div v-if="auraLoading && auraEvents.length === 0" class="aura-history-empty">
              <p>Cargando historial...</p>
            </div>

            <div v-else-if="auraEvents.length === 0" class="aura-history-empty">
              <p>Sin eventos AURA aun.</p>
              <p style="font-size: 11px; color: var(--oa-text-disabled); margin-top: 4px;">
                Completa lecciones y quizzes para ganar AURA
              </p>
            </div>

            <template v-else>
              <div
                v-for="(event, i) in auraEvents"
                :key="event.id || i"
                class="aura-event-row"
                :style="{ borderBottom: i < auraEvents.length - 1 ? '1px solid var(--oa-border)' : undefined }"
              >
                <div
                  class="aura-event-icon"
                  :style="{ background: `color-mix(in srgb, ${sourceColors[event.source] ?? '#94a3b8'} 15%, transparent)` }"
                >
                  <q-icon :name="sourceIcons[event.source] ?? 'star'" size="14px" :style="{ color: sourceColors[event.source] ?? '#94a3b8' }" />
                </div>
                <div class="aura-event-info">
                  <p class="aura-event-desc">{{ event.description }}</p>
                  <p class="aura-event-date">{{ formatDate(event.createdAt) }}</p>
                </div>
                <span
                  class="aura-event-badge"
                  :style="{ background: `color-mix(in srgb, ${sourceColors[event.source] ?? '#94a3b8'} 12%, transparent)`, color: sourceColors[event.source] ?? '#94a3b8' }"
                >
                  {{ sourceLabels[event.source] ?? event.source }}
                </span>
                <span class="aura-event-points">+{{ event.points }}</span>
              </div>

              <div v-if="auraHasMore" class="aura-load-more">
                <q-btn
                  no-caps
                  flat
                  :loading="auraLoading"
                  label="Cargar mas"
                  style="color: var(--oa-gold-full); font-size: 13px;"
                  @click="loadMoreAura"
                />
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { api } from 'boot/axios';

// --- Types ---
interface GamificationProfile {
  xp: number;
  level: number;
  levelName: string;
  xpToNextLevel: number;
  xpProgress: number;
  currentStreak: number;
  longestStreak: number;
  achievementsUnlocked: number;
  recentAchievements: Achievement[];
  achievements?: Achievement[];
  collectibles?: Collectible[];
}

interface Achievement {
  id: string;
  name: string;
  greekName: string | null;
  description: string;
  icon: string | null;
  category: string;
  xpReward: number;
  isSecret: boolean;
  isUnlocked: boolean;
}

interface Collectible {
  id: string;
  name: string;
  greekName: string | null;
  description: string;
  type: string;
  iconSymbol: string | null;
  borderClass: string | null;
  rankTitle: string | null;
  isEarned: boolean;
}

interface LeaderboardEntry {
  userId: string;
  user: { firstName: string; lastName: string; avatarUrl: string | null };
  xp: number;
  rank: number;
  lessonsCompleted: number;
  quizzesPassed: number;
  sourceBreakdown: Record<string, number>;
  isCurrentUser?: boolean;
  totalBreakdown?: number;
}

interface AuraSource {
  source: string;
  total: number;
}

interface AuraEvent {
  id?: string;
  source: string;
  points: number;
  description: string;
  createdAt: string;
}

interface AchievementCategory {
  key: string;
  label: string;
  items: Achievement[];
  unlockedCount: number;
}

// --- Constants ---
const CATEGORY_LABELS: Record<string, string> = {
  streak: 'Racha',
  xp: 'Experiencia',
  course: 'Cursos',
  social: 'Comunidad',
  special: 'Especiales',
};

const categoryIcons: Record<string, string> = {
  streak: 'local_fire_department',
  xp: 'bolt',
  course: 'menu_book',
  social: 'visibility',
  special: 'auto_awesome',
};

const sourceColors: Record<string, string> = {
  lesson: '#60a5fa',
  quiz: '#4ade80',
  achievement: '#fbbf24',
  exam: '#c084fc',
  community: '#f472b6',
  streak: '#fb923c',
  manual: '#94a3b8',
};

const sourceLabels: Record<string, string> = {
  lesson: 'Leccion',
  quiz: 'Quiz',
  achievement: 'Logro',
  exam: 'Examen',
  community: 'Comunidad',
  streak: 'Racha',
  manual: 'Manual',
};

const sourceIcons: Record<string, string> = {
  lesson: 'article',
  quiz: 'quiz',
  achievement: 'emoji_events',
  exam: 'school',
  community: 'groups',
  streak: 'local_fire_department',
  manual: 'edit',
};

const tabs = [
  { key: 'logros', label: 'Logros', icon: 'emoji_events' },
  { key: 'ranking', label: 'Ranking', icon: 'leaderboard' },
  { key: 'aura', label: 'AURA', icon: 'bolt' },
];

const scopeOptions = [
  { value: 'sede', label: 'Mi Sede' },
  { value: 'global', label: 'Global' },
];

const periodOptions = [
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'alltime', label: 'Historico' },
];

const rarityLegend = [
  { label: 'Comun', color: 'var(--color-charcoal-mist)' },
  { label: 'Raro', color: '#60a5fa' },
  { label: 'Epico', color: '#c084fc' },
  { label: 'Legendario', color: 'var(--color-terracotta)' },
];

const ICON_MAP: Record<string, string> = {
  'target': 'track_changes',
  'award': 'military_tech',
  'book-open': 'menu_book',
  'star': 'star',
  'flame': 'local_fire_department',
  'eye': 'visibility',
  'shield': 'shield',
  'medal': 'military_tech',
  'crown': 'workspace_premium',
  'trophy': 'emoji_events',
  'zap': 'bolt',
};

// --- State ---
const loading = ref(true);
const error = ref<string | null>(null);
const activeTab = ref('logros');

// Profile
const profile = ref<GamificationProfile>({
  xp: 0,
  level: 0,
  levelName: 'Mortal',
  xpToNextLevel: 100,
  xpProgress: 0,
  currentStreak: 0,
  longestStreak: 0,
  achievementsUnlocked: 0,
  recentAchievements: [],
});

// Achievements
const categories = ref<AchievementCategory[]>([]);
const collectibles = ref<Collectible[]>([]);
const totalVisible = ref(0);

// Leaderboard
const lbScope = ref('sede');
const lbPeriod = ref('weekly');
const lbEntries = ref<LeaderboardEntry[]>([]);
const lbLoading = ref(false);

// AURA
const auraSources = ref<AuraSource[]>([]);
const auraEvents = ref<AuraEvent[]>([]);
const auraHasMore = ref(false);
const auraCursor = ref<string | null>(null);
const auraLoading = ref(false);
const auraTabLoaded = ref(false);

// --- Computed ---
const completionPct = computed(() => {
  if (totalVisible.value === 0) return 0;
  return Math.round((profile.value.achievementsUnlocked / totalVisible.value) * 100);
});

const auraTotalPoints = computed(() =>
  auraSources.value.reduce((sum, s) => sum + s.total, 0),
);

// --- Helpers ---
function getRarity(xpReward: number) {
  if (xpReward >= 500) return { tier: 'Legendario', color: 'var(--color-terracotta)', glow: '0 0 24px rgba(232,184,75,0.25)' };
  if (xpReward >= 150) return { tier: 'Epico', color: '#c084fc', glow: '0 0 20px rgba(192,132,252,0.2)' };
  if (xpReward >= 75) return { tier: 'Raro', color: '#60a5fa', glow: '0 0 16px rgba(96,165,250,0.15)' };
  return { tier: 'Comun', color: 'var(--color-charcoal-mist)', glow: 'none' };
}

function isHidden(ach: Achievement): boolean {
  return ach.isSecret && !ach.isUnlocked;
}

function achievementIcon(icon: string | null, category: string): string {
  if (icon && ICON_MAP[icon]) return ICON_MAP[icon];
  return categoryIcons[category] ?? 'star';
}

function formatNumber(n: number): string {
  return n.toLocaleString('es-AR');
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// --- Data fetching ---
async function fetchAllData() {
  loading.value = true;
  error.value = null;

  try {
    const [profileRes, lbRes, auraBySourceRes] = await Promise.all([
      api.get<GamificationProfile>('/gamification/profile'),
      api.get<LeaderboardEntry[]>('/gamification/leaderboard', {
        params: { period: lbPeriod.value, scope: lbScope.value },
      }),
      api.get<AuraSource[]>('/gamification/aura/by-source'),
    ]);

    // Profile
    profile.value = profileRes.data;

    // Build categories from achievements array
    const allAchievements = profileRes.data.achievements ?? [];
    const catMap = new Map<string, Achievement[]>();
    for (const a of allAchievements) {
      if (!catMap.has(a.category)) catMap.set(a.category, []);
      catMap.get(a.category)!.push(a);
    }
    categories.value = [...catMap.entries()].map(([key, items]) => ({
      key,
      label: CATEGORY_LABELS[key] ?? key,
      items,
      unlockedCount: items.filter((a) => a.isUnlocked).length,
    }));

    totalVisible.value = allAchievements.filter((a) => !a.isSecret || a.isUnlocked).length;

    // Collectibles
    collectibles.value = profileRes.data.collectibles ?? [];

    // Leaderboard
    lbEntries.value = (lbRes.data ?? []).map((entry) => {
      const breakdown = entry.sourceBreakdown ?? {};
      return {
        ...entry,
        totalBreakdown: Object.values(breakdown).reduce((sum, v) => sum + (v ?? 0), 0),
      };
    });

    // AURA sources
    auraSources.value = auraBySourceRes.data ?? [];
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error al cargar datos de gamificacion';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

async function fetchLeaderboard() {
  lbLoading.value = true;
  try {
    const { data } = await api.get<LeaderboardEntry[]>('/gamification/leaderboard', {
      params: { period: lbPeriod.value, scope: lbScope.value },
    });
    lbEntries.value = (data ?? []).map((entry) => {
      const breakdown = entry.sourceBreakdown ?? {};
      return {
        ...entry,
        totalBreakdown: Object.values(breakdown).reduce((sum, v) => sum + (v ?? 0), 0),
      };
    });
  } catch {
    // Silent fail for leaderboard refetch
  } finally {
    lbLoading.value = false;
  }
}

async function fetchAuraHistory() {
  auraLoading.value = true;
  try {
    const params: Record<string, string | number> = { limit: 20 };
    if (auraCursor.value) params.cursor = auraCursor.value;

    const { data } = await api.get<{ events: AuraEvent[]; hasMore: boolean; nextCursor: string | null }>('/gamification/aura/history', { params });
    if (auraCursor.value) {
      auraEvents.value = [...auraEvents.value, ...data.events];
    } else {
      auraEvents.value = data.events;
    }
    auraHasMore.value = data.hasMore;
    auraCursor.value = data.nextCursor ?? null;
  } catch {
    // Silent fail
  } finally {
    auraLoading.value = false;
  }
}

function loadMoreAura() {
  fetchAuraHistory();
}

// --- Watchers ---
watch([() => lbPeriod.value, () => lbScope.value], () => {
  fetchLeaderboard();
});

watch(() => activeTab.value, (tab) => {
  if (tab === 'aura' && !auraTabLoaded.value) {
    auraTabLoaded.value = true;
    fetchAuraHistory();
  }
});

// --- Init ---
onMounted(fetchAllData);
</script>

<style scoped lang="scss">
.achievements-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.achievements-container {
  max-width: 960px;
  width: 100%;
}

// === Skeleton ===
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

// === Empty / Error ===
.achievements-empty {
  text-align: center;
  padding: 64px 32px;
}

.empty-text {
  color: var(--oa-text-muted);
  margin-top: 12px;
}

// === Header ===
.ach-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.ach-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
  line-height: 1.3;
}

.ach-subtitle {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.ach-header-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--oa-text-muted);

  strong {
    font-family: var(--oa-font-mono);
    color: var(--oa-text-primary);
  }
}

// === Tabs ===
.ach-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 4px;
}

.ach-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--oa-text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    color: var(--oa-text-secondary);
    background: var(--oa-bg-3);
  }
}

.ach-tab-active {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
}

// === Profile card ===
.profile-card {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  margin-bottom: 20px;
}

.profile-card-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.profile-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
}

.profile-level-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.profile-level-sub {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.profile-card-right {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 24px;
}

.xp-bar-wrap {
  flex: 1;
}

.xp-bar-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--oa-text-muted);
  margin-bottom: 4px;

  span:last-child {
    font-family: var(--oa-font-mono);
    font-weight: 700;
    color: var(--oa-gold-full);
  }
}

.xp-bar-track {
  height: 8px;
  border-radius: 4px;
  background: var(--oa-bg-3);
  overflow: hidden;
}

.xp-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--oa-gold-mid), var(--oa-gold-full));
  transition: width 0.5s ease;
}

.xp-bar-hint {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 3px 0 0;
}

.streak-display {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.streak-count {
  font-size: 20px;
  font-weight: 700;
  font-family: var(--oa-font-mono);
  color: var(--oa-text-primary);
}

.streak-label {
  font-size: 11px;
  color: var(--oa-text-muted);
}

// === Progress bar section ===
.progress-bar-section {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.progress-bar-header {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-muted);
  margin-bottom: 6px;
}

.progress-bar-pct {
  font-family: var(--oa-font-mono);
  font-weight: 700;
  color: var(--oa-gold-full);
}

.progress-bar-track {
  height: 8px;
  border-radius: 4px;
  background: var(--oa-bg-3);
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--oa-gold-mid), var(--oa-gold-full));
  transition: width 0.7s ease;
}

.rarity-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  justify-content: flex-end;
}

.rarity-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: var(--oa-text-disabled);
}

.rarity-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

// === Achievement categories ===
.ach-category {
  margin-bottom: 28px;
}

.cat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.cat-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cat-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.cat-count {
  font-size: 12px;
  font-family: var(--oa-font-mono);
  color: var(--oa-text-disabled);
}

.cat-progress-track {
  width: 96px;
  height: 6px;
  border-radius: 3px;
  background: var(--oa-bg-3);
  overflow: hidden;
}

.cat-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

// === Achievement grid ===
.ach-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.ach-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
}

.ach-locked {
  opacity: 0.45;
  filter: grayscale(0.6);
}

.ach-unlocked {
  opacity: 1;
}

.ach-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ach-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 10px 0 0;
}

.ach-greek {
  font-size: 11px;
  font-style: italic;
  margin: 2px 0 0;
}

.ach-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 6px 0 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.ach-reward-row {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.ach-reward-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.badge-gold {
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  color: var(--oa-gold-full);
}

.badge-outline {
  border: 1px solid var(--oa-border);
  color: var(--oa-text-disabled);
}

.ach-rarity {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 9px;
  font-weight: 500;
}

// === Collectibles ===
.collectibles-section {
  margin-top: 32px;
}

.section-heading {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.collectibles-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.collectible-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  opacity: 0.45;
  filter: grayscale(0.6);
  transition: all 0.2s;
}

.collectible-earned {
  opacity: 1;
  filter: none;
  border-color: var(--oa-gold-dim);
}

.collectible-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
}

.collectible-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.collectible-greek {
  font-size: 11px;
  font-style: italic;
  color: var(--oa-gold-mid);
  margin: 2px 0 0;
}

.collectible-rank {
  font-size: 10px;
  font-weight: 700;
  color: var(--oa-gold-full);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 4px 0 0;
}

.collectible-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 6px 0 0;
  line-height: 1.4;
}

// === Leaderboard ===
.lb-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.lb-toggle-group {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--oa-border);
}

.lb-toggle {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--oa-bg-2);
  color: var(--oa-text-muted);
}

.lb-toggle-active {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
}

.lb-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  overflow: hidden;
}

.lb-empty {
  padding: 32px;
  text-align: center;
}

.lb-row-skeleton {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--oa-border);

  &:last-child {
    border-bottom: none;
  }
}

.lb-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  transition: background 0.15s;
}

.lb-row-me {
  background: rgba(232, 184, 75, 0.04);
}

.lb-rank {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  color: var(--oa-text-disabled);
}

.lb-rank-top {
  background: rgba(232, 184, 75, 0.08);
  color: var(--oa-gold-mid);
}

.lb-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
  color: var(--oa-text-muted);
  flex-shrink: 0;
}

.lb-info {
  flex: 1;
  min-width: 0;
}

.lb-name {
  font-size: 13px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lb-me-badge {
  font-size: 11px;
  margin-left: 4px;
  color: var(--oa-gold-dim);
}

.lb-stats-row {
  display: flex;
  gap: 12px;
  margin-top: 2px;

  span {
    font-size: 10px;
    color: var(--oa-text-disabled);
  }
}

.lb-breakdown-bar {
  display: flex;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 6px;
}

.lb-breakdown-segment {
  height: 100%;
}

.lb-xp {
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

// === AURA tab ===
.aura-breakdown-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.aura-section-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.aura-empty-sources {
  text-align: center;
  padding: 24px;

  p {
    color: var(--oa-text-muted);
    font-size: 13px;
    margin: 0;
  }
}

.aura-sources-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.aura-source-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aura-source-label {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100px;
  flex-shrink: 0;
  font-size: 12px;
  color: var(--oa-text-secondary);
}

.aura-source-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.aura-source-bar-track {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: var(--oa-bg-3);
  overflow: hidden;
}

.aura-source-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.aura-source-value {
  width: 48px;
  text-align: right;
  font-size: 12px;
  font-family: var(--oa-font-mono);
  font-weight: 600;
  color: var(--oa-text-primary);
  flex-shrink: 0;
}

// === AURA history ===
.aura-history-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  overflow: hidden;
}

.aura-history-card > .aura-section-title {
  padding: 16px 20px 0;
}

.aura-history-empty {
  padding: 32px;
  text-align: center;

  p {
    color: var(--oa-text-muted);
    font-size: 13px;
    margin: 0;
  }
}

.aura-event-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  transition: background 0.1s;
}

.aura-event-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.aura-event-info {
  flex: 1;
  min-width: 0;
}

.aura-event-desc {
  font-size: 13px;
  color: var(--oa-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aura-event-date {
  font-size: 11px;
  color: var(--oa-text-disabled);
  margin: 2px 0 0;
}

.aura-event-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.aura-event-points {
  font-size: 13px;
  font-weight: 700;
  color: var(--oa-gold-mid);
  flex-shrink: 0;
}

.aura-load-more {
  text-align: center;
  padding: 8px;
  border-top: 1px solid var(--oa-border);
}

// === Mobile ===
@media (max-width: 640px) {
  .ach-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .profile-card {
    flex-direction: column;
    gap: 16px;
  }

  .profile-card-right {
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  .lb-filters {
    flex-direction: column;
  }
}
</style>
